import React, { useState } from 'react';
import Step1New from './step1New';
import Step2Details from './Step2Details';
import Step3Medias from './Step3Medias';
import Step4Features from './Step4Features';
import Step5Preview from './Step5Preview';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { showToast } from '../../../../components/common/notification/Notification';
import { useNavigate } from "react-router-dom";
import { getUserTypeName } from '../../../navigation/adminNavigation';

const PropertyForm = () => {
  const navigate = useNavigate();

  const loggedInUser = useSelector((state) => state.auth.user);
  const authState = useSelector((state) => state.auth);
  const assetUrl = process.env.REACT_APP_ASSET_URL;
  const token = loggedInUser?.refreshToken || loggedInUser?.token; 
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [step, setStep] = useState(1);
  const [propertyForm, setPropertyForm] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = [
    { number: 1, title: 'Type & Purpose' },
    { number: 2, title: 'Details & Location' },
    { number: 3, title: 'Media & Uploads' },
    { number: 4, title: 'Features & Amenities' },
    { number: 5, title: 'Review & Publish' },
  ];

  const BUY_PURPOSE_ID = 1; // buy id

  const handleNext = () => {
    if (
      step === 2 &&
      propertyForm.propertyPurpose === BUY_PURPOSE_ID &&
      (!propertyForm.images || propertyForm.images.length === 0)
    ) {
      setStep(4); // Skip medias
    } else if (step < steps.length) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  // Step 5: Update button handler
  const handleUpdate = () => {
    setStep(1);
  };

  // Step 5: Submit button handler
  const handleSubmit = async () => { 
    if (isSubmitting) return;

    setIsSubmitting(true);
    let images = (propertyForm.images || []).map((img) => ({
      image_url: (img.url || "").replace(assetUrl, ""),
    }));

    const videos = (propertyForm.videos || []).map((video) => ({
      name: video.name || null,
      description: null,
      url_path: (video.url || "").replace(assetUrl, ""),
    })); 

    const attachments = (propertyForm.attachments || []).map((a) => ({
      name: a.name || null,
      attachment_url: (a.url || "").replace(assetUrl, ""),
    }));

    const payload = {
      city_name: propertyForm.cityName || '',
      state_name: propertyForm.stateName || '',
      country_name: "India",
      area_name: propertyForm.area || '',
      property_type_id: propertyForm.propertyType || null,      
      purpose_id: propertyForm.propertyPurpose || null,
      property_status_id: 5,
      sub_type_id: propertyForm.propertySubtype || null,        
      structure: propertyForm.structure || {},                  
      feature: propertyForm.feature || {},                      
      images,
      videos,
      attachments, 
      tags: propertyForm.tags || '',
      name: propertyForm.name || '',
      description: propertyForm.description || '',
      address: propertyForm.address || '',
      land_mark: propertyForm.land_mark || '',
      pin_code: propertyForm.pin_code || '',
      price: propertyForm.price || '', 
      land_type_id: propertyForm.landType || null,              
      loan_availability: propertyForm.loanAvailability,
      are: propertyForm.are || '',
      sub_sub_type_id: propertyForm.propertySubSubType || null, 
      lead_id: 0,
      active: true, 
      featured_property: propertyForm.featured_property,
    };

    try {
      const response = await axios.post(`${baseUrl}/property/addProperty`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.statusType === 'SUCCESS') {
        showToast('Property created successfully!' , { type: 'success' });
        setTimeout(() => {
          const userType = getUserTypeName(authState);
          if (userType === 'CUSTOMER') {
            navigate("/customer/myspace");
          } else if (userType === 'PARTNER') {
            navigate("/partner/my-properties");
          } else {
            navigate("/property");
          }
        }, 1500);
      } else {
        setIsSubmitting(false);
        showToast(response.data.text || 'Failed to submit property', { type: 'error' });
      }
    } catch (error) {
      setIsSubmitting(false);
      showToast('Failed to submit property data', { type: 'error' });
      console.error("Error submitting property form:", error);
    }
  };

  const renderStepComponent = () => {
    switch (step) {
      case 1:
        return <Step1New form={propertyForm} setForm={setPropertyForm} nextStep={handleNext} />;
      case 2:
        return <Step2Details form={propertyForm} setForm={setPropertyForm} nextStep={handleNext} prevStep={handleBack} />;
      case 3:
        return (
          <Step3Medias 
            form={propertyForm}
            setForm={setPropertyForm}
            nextStep={handleNext}
            prevStep={handleBack}
          />
        );
      case 4:
        return (
          <Step4Features
            form={propertyForm}
            setForm={setPropertyForm}
            nextStep={handleNext}
            prevStep={handleBack}
          />
        );
      case 5:
        return (
          <div>
            <Step5Preview 
              form={propertyForm} 
              prevStep={handleBack} 
              onUpdate={handleUpdate} 
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const progressPercent = ((step - 1) / (steps.length - 1)) * 100;

  return (
    <div className="container py-4" style={{ maxWidth: "1080px" }}>
      <div className="mb-4">
        <h2 className="fw-bold mb-1" style={{ color: "#1e293b", fontSize: "1.75rem" }}>Create New Property</h2>
        <p className="text-muted small mb-0">Fill in the property details to publish your listing across the marketplace.</p>
      </div>

      {/* Modern Stepper Indicator */}
      <div className="card border-0 shadow-sm p-3 mb-4 rounded-3" style={{ background: "#ffffff" }}>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          {steps.map((s) => {
            const isCompleted = step > s.number;
            const isCurrent = step === s.number;
            return (
              <div 
                key={s.number} 
                className={`d-flex align-items-center gap-2 px-3 py-2 rounded-2 ${
                  isCurrent ? 'bg-primary text-white shadow-sm' : isCompleted ? 'bg-light text-success fw-medium' : 'text-muted'
                }`}
                style={{ fontSize: "0.85rem", transition: "all 0.2s" }}
              >
                <span 
                  className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
                    isCurrent ? 'bg-white text-primary' : isCompleted ? 'bg-success text-white' : 'bg-secondary text-white'
                  }`}
                  style={{ width: "22px", height: "22px", fontSize: "0.75rem", fontWeight: 700 }}
                >
                  {isCompleted ? "✓" : s.number}
                </span>
                <span>{s.title}</span>
              </div>
            );
          })}
        </div>
        <div className="progress" style={{ height: "6px" }}>
          <div 
            className="progress-bar bg-primary" 
            role="progressbar"
            style={{ width: `${progressPercent}%`, transition: "width 0.3s ease" }}
            aria-valuenow={progressPercent}
            aria-valuemin="0"
            aria-valuemax="100"
          />
        </div>
      </div>

      <div className="card border-0 shadow-sm p-4 rounded-3" style={{ background: "#ffffff" }}>
        {renderStepComponent()}
      </div>
    </div>
  );
};

export default PropertyForm;